/**
 * The only place env vars are read. Nothing else touches `import.meta.env`,
 * so a missing var fails loudly here instead of silently at a call site.
 * Never hardcode a URL or key anywhere in the app.
 */
function required(name: string, value: string | undefined): string {
  if (!value) {
    throw new Error(`Missing required env var ${name}. Copy .env.example to .env.local.`)
  }
  return value
}

function optional(value: string | undefined, fallback: string): string {
  return value && value.length > 0 ? value : fallback
}

const apiBaseUrl = required('VITE_API_BASE_URL', import.meta.env.VITE_API_BASE_URL).replace(
  /\/+$/,
  '',
)

export const config = {
  apiBaseUrl,
  /**
   * The API's non-staff tree (`/api/v1`, no `/admin`). The backend reserves
   * `/admin` for staff authentication, so the hotel-facing Secure Hotel
   * Portal is served beside it at `/api/v1/hotel-portal/...`. Defaults to
   * `apiBaseUrl` with a trailing `/admin` removed; set
   * `VITE_PUBLIC_API_BASE_URL` only when the two are deployed apart.
   * Used through `publicApi` in `gateway/client.ts`.
   */
  publicApiBaseUrl: optional(
    import.meta.env.VITE_PUBLIC_API_BASE_URL,
    apiBaseUrl.replace(/\/admin$/, ''),
  ).replace(/\/+$/, ''),
  publicSiteUrl: optional(import.meta.env.VITE_PUBLIC_SITE_URL, '').replace(/\/+$/, ''),
  /**
   * The hotel-facing Secure Hotel Portal's link prefix, shown (masked) in the
   * Send modal's link preview. Display only: the real link is generated by the
   * backend and exists only in the email. Empty → the preview shows the mask alone.
   */
  securePortalUrl: optional(import.meta.env.VITE_SECURE_PORTAL_URL, '').replace(/\/+$/, ''),
  map: {
    /**
     * Google Maps browser key. The Maps JavaScript API only accepts a key sent
     * from the browser, so this one is PUBLIC by design — like every `VITE_*`
     * value it is inlined into the bundle and readable by anyone.
     *
     * That is acceptable *only* because the key is restricted server-side, in
     * the Google Cloud Console, and those restrictions are the actual control:
     *
     *   1. Application restriction: HTTP referrers, limited to our own origins.
     *   2. API restriction: Maps JavaScript API + Places API only.
     *   3. A billing budget/quota cap, so a leaked key cannot run up a bill.
     *
     * Unset (the default) disables the picker rather than loading a broken map
     * — see `isMapConfigured`.
     */
    googleMapsApiKey: optional(import.meta.env.VITE_GOOGLE_MAPS_API_KEY, ''),
    /** Map id for cloud-based styling; Google requires one for Advanced Markers. */
    mapId: optional(import.meta.env.VITE_GOOGLE_MAPS_MAP_ID, 'DEMO_MAP_ID'),
  },
  sentryDsn: optional(import.meta.env.VITE_SENTRY_DSN, ''),
  appVersion: optional(import.meta.env.VITE_APP_VERSION, 'dev'),
  isDev: import.meta.env.DEV,
} as const

/**
 * Storage key for the session tokens — the ONLY thing we persist. Holds
 * `SessionTokens`; never user data, app state or PII.
 *
 * The session lives in `localStorage`. The same key is still read from
 * `sessionStorage` so users signed in under the previous build are not logged
 * out by the deploy. The security trade-off involved (TC-UI-L09, still open) is
 * documented in the session section of `src/gateway/client.ts`.
 */
export const AUTH_SESSION_STORAGE_KEY = 'turon.admin.session'

/**
 * Storage key for the hotel portal's session token — a different credential
 * from the staff session above, read and written only by `gateway/client.ts`.
 * `sessionStorage` on purpose: the portal reveals payment cards, so its
 * session dies with the tab.
 */
export const HOTEL_PORTAL_SESSION_STORAGE_KEY = 'turon.hotelPortal.session'

/** Listing defaults. Per-user override lives in User Settings (results per page). */
export const DEFAULT_PAGE_SIZE = 25
export const PAGE_SIZE_OPTIONS = [10, 25, 50, 100] as const

/** Debounce for search inputs that hit the API. */
export const SEARCH_DEBOUNCE_MS = 350
